<template>
  <video
    :id="videoId"
    ref="video"
    class="video-js mx-auto vjs-big-play-centered vjs-show-big-play-button-on-pause"
    :class="divClass"
    :poster="poster"
    playsinline
  />
</template>

<script>
import { useLocalStorage } from '@vueuse/core'
import $ from 'jquery'
import each from 'lodash/each'
import findIndex from 'lodash/findIndex'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import random from 'lodash/random'
import sortBy from 'lodash/sortBy'
import videojs from 'video.js'
import contextmenuUI from 'videojs-contextmenu-ui'
import { computed, markRaw, ref } from 'vue'
import STORAGE_KEYS from '@/common/storage-keys.js'
import constants from '@/generated/constants.js'
import { download_file, is_touchable_device } from '../../utils'
import BackButton from './video.js/back-button'
import ControlBarPlugin from './video.js/control-bar-plugin'
import { CopyLinkButton } from './video.js/copy-link-button'
import DubbUiPlugin from './video.js/dubb-ui-plugin'
import ForwardButton from './video.js/forward-button'
import LogoPlugin from './video.js/logo-plugin'
import QualityMenuPlugin from './video.js/quality-menu-plugin'
import RatePlugin from './video.js/rate-plugin'
import { ResumePlaybackPlugin } from './video.js/resume-playback-plugin'
import SettingsMenuPlugin from './video.js/settings-menu-plugin'
import socialSharePlugin from './video.js/social-share-plugin'
import turnOnSoundButtonPlugin from './video.js/turn-on-sound-plugin'
import { vjs_El } from './video.js/utils'
import ZoomPlugin from './video.js/zoom-plugin'
import 'videojs-youtube'

export default {
  name: 'VideoJs',
  props: {
    setup: {},
    downloadOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          videoUrl: null,
          transcriptUrl: null,
        }
      },
    },
    logoOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          img: null,
          position: 'TL',
        }
      },
    },
    elId: {},
    poster: {},
    source: {},
    defaultRes: {
      type: String,
      default: '360',
    },
    turnOnSoundButton: {
      default: false,
    },
    posterBgColor: {
      type: String,
      default: '#ededed',
    },
    resolutions: {
      default: () => {
        return {}
      },
    },
    divClass: {},
    subtitlePath: {},
    contextMenu: {
      default: () => {
        return []
      },
    },
    referUrl: {},
    brandingUrl: {},
    shareOptions: {
      default: () => {
        return {}
      },
    },
    resumePlaybackId: {},
    shouldHidePlayerControlBar: {
      type: Boolean,
      default: false,
    },
    shouldHideSeekButtons: {
      type: Boolean,
      default: false,
    },
    shouldShowCopyLinkButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['ready'],
  setup() {
    const captionEnabled = useLocalStorage(STORAGE_KEYS.VIDEO_PLAYER.CAPTION_ENABLED, true)
    const captionTrackMode = computed(() => captionEnabled.value ? 'showing' : 'disabled')
    const isReloadingSubtitle = ref(false)

    return {
      captionEnabled,
      captionTrackMode,
      isReloadingSubtitle,
    }
  },
  data() {
    return {
      player: null,
      autoplaying: true,
      videoId: this.elId,
    }
  },
  computed: {
    simpleSrcs() {
      if (this.resolutionSrcs.length > 0 || (this.setup.sources && this.setup.sources.length > 0)) {
        return []
      }
      if (isObject(this.source)) {
        const ret = []
        each(this.source, (src, type) => {
          ret.push({ src, type })
        })
        return ret
      }

      if (!this.source) {
        return []
      }

      let type
      if (this.source.endsWith('.m3u8')) {
        type = 'application/x-mpegURL'
      }
      else {
        type = 'video/mp4'
      }
      return [{ type, src: this.source }]
    },
    resolutionSrcs() {
      let srcs = sortBy(map(this.resolutions, (item, key) => {
        return {
          src: item,
          type: item.endsWith('.m3u8') ? 'application/x-mpegURL' : 'video/mp4',
          label: constants.GLOBAL.VIDEO_RESOLUTION_LABEL_MAP[key],
          res: constants.GLOBAL.VIDEO_RESOLUTION_RES_MAP[key],
          default: false,
        }
      }), (item) => {
        return Number.parseInt(item.res)
      })
      if (srcs.length > 0) {
        let index = findIndex(srcs, { res: this.defaultRes })
        if (index < 0) {
          index = 0
        }
        srcs[index].default = true
      }
      srcs = sortBy(srcs, item => !item.default)

      return srcs
    },
    sources() {
      if (this.setup.sources && this.setup.sources.length > 0) {
        return this.setup.sources
      }
      if (this.simpleSrcs.length > 0) {
        return this.simpleSrcs
      }
      return this.resolutionSrcs
    },
  },

  watch: {
    'setup.poster': function (newVal) {
      this.player.poster(newVal)
    },
    resumePlaybackId(newVal) {
      this.player.ResumePlaybackPlugin().dispose()
      this.player.ResumePlaybackPlugin({ uuid: newVal, playbackOffset: 5 })
    },
  },
  created() {
    if (!this.videoId) {
      this.videoId = `videoid-${random(100000, 999999)}`
    }
  },
  mounted() {
    const plugins = {
      contextmenuUI,
      ControlBarPlugin,
      DubbUiPlugin,
      LogoPlugin,
      QualityMenuPlugin,
      RatePlugin,
      ResumePlaybackPlugin,
      SettingsMenuPlugin,
      socialSharePlugin,
      turnOnSoundButtonPlugin,
      ZoomPlugin,
    }

    Object.keys(plugins).forEach((name) => {
      if (!videojs.getPlugin(name)) {
        videojs.registerPlugin(name, plugins[name])
      }
    })

    videojs.registerComponent('CopyLinkButton', CopyLinkButton)
    videojs.registerComponent('BackButton', BackButton)
    videojs.registerComponent('ForwardButton', ForwardButton)
    const setup = Object.assign({}, {
      language: 'en',
      languages: {
        en: {
          LIVE: 'LOADING',
        },
      },
      controlBar: {},
    }, this.setup)
    if (this.poster) {
      setup.poster = this.poster
    }
    if (setup.controlBar !== false && !setup.controlBar.remainingTimeDisplay) {
      setup.controlBar.remainingTimeDisplay = false
    }

    const player = videojs(this.$refs.video, setup, () => {
      let aboutLink = 'https://dubb.com/'
      if (this.referUrl) {
        aboutLink = this.referUrl
      }
      this.player.contextmenuUI({
        content: [{
          href: aboutLink,
          label: 'About Dubb',
        }].concat(this.contextMenu),
      })

      if (this.turnOnSoundButton) {
        this.player.turnOnSoundButtonPlugin()
      }

      if (setup.controls !== false) {
        this.player.ControlBarPlugin({
          pipButton: false,
        })
        this.player.QualityMenuPlugin()
        this.player.SettingsMenuPlugin({})
        this.player.RatePlugin({})
        this.player.ZoomPlugin({})
        this.player.DubbUiPlugin({})

        if (this.downloadOptions.transcriptUrl) {
          this.addSettingButton('Download Transcript', (downloadTranscriptBtnEl) => {
            downloadTranscriptBtnEl.innerHTML = 'Downloading...'
            download_file(this.downloadOptions.transcriptUrl, () => {
              downloadTranscriptBtnEl.innerHTML = 'Download Transcript'
            })
          })
        }

        if (this.downloadOptions.videoUrl) {
          this.addSettingButton('Download Video', this.downloadOptions.videoUrl)
        }

        this.addSettingButton('Report Issue', '/contact')
        this.addSettingButton('About Dubb', 'https://dubb.com/')
      }

      if (this.logoOptions.img) {
        this.player.LogoPlugin({
          img: this.logoOptions.img,
          position: this.logoOptions.position,
        })
      }

      if (this.shareOptions.url) {
        this.player.socialSharePlugin(this.shareOptions)
      }
      if (this.resumePlaybackId) {
        this.player.ResumePlaybackPlugin({ uuid: this.resumePlaybackId, playbackOffset: 5 })
      }

      this.reloadSubtitle()
    })

    this.player = markRaw(player)

    this.player.src(this.sources)

    this.player.on('ready', () => {
      const controlBar = this.player.controlBar

      if (this.player.currentWidth() > 540 && !this.shouldHideSeekButtons) {
        if (controlBar) {
          if (!controlBar.getChild('ForwardButton')) {
            controlBar.addChild('ForwardButton', {}, 2)
          }

          if (!controlBar.getChild('BackButton')) {
            controlBar.addChild('BackButton', {}, 2)
          }
        }
      }

      if (this.shouldShowCopyLinkButton) {
        if (controlBar && !controlBar.getChild('CopyLinkButton')) {
          controlBar.addChild('CopyLinkButton', {}, 15)
        }
      }

      if (this.shouldHidePlayerControlBar) {
        this.hidePlayerControlBar()
      }

      // Listen for caption track changes and save to localStorage
      this.setupCaptionStateListener()
    })

    const Button = videojs.getComponent('Button')
    const dubbBrandingButton = new Button(player, {
      controlText: 'Video powered by Dubb',
      className: 'vjs-dubb-branding',
      clickHandler: () => {
        window.open(this.brandingUrl)
      },
    })
    dubbBrandingButton.hide()
    player.addChild(dubbBrandingButton)

    this.player.on('play', () => {
      $(this.$refs.video).closest('.video-js').removeClass('vjs-show-big-play-button-on-pause')
      if (this.autoplaying) {
        this.autoplaying = false
        return
      }

      dubbBrandingButton.hide()
      this.player.options_.inactivityTimeout = 2000
    })
    this.player.on('ended', () => {
      if (!is_touchable_device()) {
        if (this.player.autoplay()) {
          this.player.autoplay(false)
        }
      }
      if (this.brandingUrl) {
        dubbBrandingButton.show()
      }
    })
    $(`#${this.videoId}`).find('.vjs-poster').css({ 'background-color': this.posterBgColor })
    this.$emit('ready', this.player)
  },
  methods: {
    reload() {
      this.player.poster(this.setup.poster ? this.setup.poster : this.poster)

      this.reloadSubtitle()
      this.player.src(this.sources)
      this.player.trigger('reload', this.sources)
    },
    reloadSubtitle() {
      const tracks = this.player.remoteTextTracks()

      for (let i = 0; i < tracks.length; i++) {
        this.player.removeRemoteTextTrack(tracks[i])
      }

      if (this.subtitlePath) {
        this.player.addRemoteTextTrack({
          src: this.subtitlePath,
          kind: 'subtitles',
          label: 'English',
          srclang: 'en',
          default: this.captionEnabled,
          mode: this.captionTrackMode,
        }, true)
      }
    },

    addSettingButton(name, cb) {
      const btnEl = vjs_El('li', 'vjs-settings-item', name)
      btnEl.ariaDisabled = 'false'
      btnEl.ariaLabel = name
      btnEl.role = 'menuitem'

      btnEl.onclick = btnEl.ontouchend = (e) => {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (isString(cb)) {
          window.open(cb)
        }
        else {
          cb(btnEl)
        }
      }

      this.player.SettingsMenuPlugin().addMenu(name, btnEl, null)
    },

    hidePlayerControlBar() {
      for (const child of this.player.controlBar.children()) {
        if (child.name() !== 'PlayToggle') {
          child.hide()
        }
      }
    },

    handleCaptionTrackChange() {
      const tracks = this.player.remoteTextTracks()
      let isEnabled = false

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        if ((track.kind === 'subtitles' || track.kind === 'captions') && track.mode === 'showing') {
          isEnabled = true
          break
        }
      }

      // Update the reactive localStorage property
      this.captionEnabled = isEnabled
    },

    setupCaptionStateListener() {
      // Listen directly to remoteTextTracks change event
      this.player.remoteTextTracks().addEventListener('change', this.handleCaptionTrackChange)
    },
  },
}
</script>

<style lang="scss" scoped>
  .video-js {
    :deep() {
      // Disable tooltips on captions popup menu items, it looks weird and unnecessary
      .vjs-subs-caps-button .vjs-menu .vjs-control-text {
        display: none !important;
      }

      .vjs-fontawesome {
        .vjs-icon-placeholder::before {
          -webkit-font-smoothing: antialiased;
          font: var(--fa-font-light);
          font-weight: 300;
          cursor: pointer;
          font-size: 16px;
          display: inline-block;
          line-height: 32px;
        }
      }

      .vjs-dubb-branding {
        position: absolute;
        background: rgba(0, 0, 0, 0.3);
        color: white;
        padding: 0.8rem 1rem;
        bottom: 5%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 5px !important;
        cursor: pointer;
        border: none;

        .vjs-control-text {
          font-size: 14px;
          position: static;
          width: auto;
          white-space: nowrap;
          display: block !important;
        }
      }

      .vjs-forward-button,
      .vjs-back-button {
        &::after {
          content: '15';
          font-size: 14px;
          position: absolute;
          width: 100%;
          text-align: center;
          left: 0;
          transform: scale(0.4);
        }
      }

      .vjs-forward-button {
        .vjs-icon-placeholder::before {
          content: '\f01e';
        }
      }

      .vjs-back-button {
        margin-left: 10px;

        .vjs-icon-placeholder {
          &::before {
            transform: scale(-1, 1);
            content: '\f01e';
          }
        }
      }

      .vjs-copy-link-button {
        .vjs-icon-placeholder::before {
          content: '\f0c1';
        }
      }

      .vjs-turn-on-sound {
        position: absolute;
        background: rgba(255,255,255,0.8);
        color: black;
        padding: 0.8rem 1rem;
        inset: 0;
        margin: auto;
        border-radius: 5px;
        border-width: 1px;
        cursor: pointer;
        height: 48px;
        width: 160px;

        .vjs-control-text {
          font-size: 14px;
          &:before {
            font: var(--fa-font-light);
            content: "\f028";
            margin-right: 5px;
          }
          position: static;
          width: auto;
          white-space: nowrap;
        }
      }

      .vjs-social-share-buttons {
        position: absolute;
        background: rgba(255,255,255,.8);
        color: white;
        padding: .8rem .7rem .3rem;
        top: 2%;
        right: 1%;
        border-radius: 5px !important;

        .vjs-social-share-button {
          .vjs-icon-placeholder::before {
            -webkit-font-smoothing: antialiased;
            font: var(--fa-font-brands);
            font-weight: 300;
            cursor: pointer;
            font-size: 16px;
            display: inline-block;
            line-height: 32px;
          }
        }
        .vjs-social-share-close-button {
          text-align: center;
          line-height: 14px;
          .vjs-control-text {
            height: auto;
            width: auto;
            font-size: 14px;
            position: unset;
          }
        }
        .vjs-social-share-facebook {
          color: #3b5998;
          .vjs-icon-placeholder::before {
            content: '\f082';
          }
        }
        .vjs-social-share-twitter {
          color: #1da1f2;
          .vjs-icon-placeholder::before {
            content: '\f081';
          }
        }
        .vjs-social-share-linkedin {
          color: #0077b5;
          .vjs-icon-placeholder::before {
            content: '\f08c';
          }
        }
      }
    }

    &.vjs-ended:deep() {
      .vjs-poster {
        display: inline-block !important;
      }
    }
  }
</style>
